/**
 * The clearance cross-section (Profile tab): what the cloud actually contains
 * at one station, drawn behind the envelope it is being judged against.
 *
 * This replaces a static drawing of the profile polygon with the station
 * dock's Section idea, adapted to the one structural difference between the
 * two analyses. Station volumes compares two SURFACES, so its section draws
 * two lines with the cut and fill hatched between them. A clearance run has no
 * surface pair — it has a cloud and a gauge — so the section draws the cloud's
 * own density and lights the cells that are inside the gauge. A per-offset
 * min/max band would have been the closer mirror and the wrong drawing: a wire
 * above an empty gap and a solid wall reduce to the same two numbers.
 *
 * Two layers, deliberately:
 *  - a CANVAS for the grid. At 0.05 m cells a station is ~26 000 cells, and
 *    26 000 SVG rects is a layout the browser re-lays-out on every hover.
 *  - an SVG overlay for the envelope, axes, markers and the hover readout —
 *    the parts that must stay crisp at any zoom and want real text.
 *
 * The arithmetic is in `@/lib/clearance/sectionModel`; this file is the
 * drawing, the interaction and the copy.
 */
import { LoadingBadge, LoadingBadgeKeyframes } from '@/components/ui/LoadingBadge';
import { useClearanceSection } from '@/hooks/useClearance';
import { CLEARANCE_FILL } from '@/lib/clearance/chart';
import {
  colAt,
  density,
  heightAt,
  offsetAt,
  peakCount,
  profilePolygonPoints,
  rowAt,
  sectionCellsAt,
  sectionTicks,
  sectionViewport,
  toPxX,
  toPxY,
} from '@/lib/clearance/sectionModel';
import type { ClearanceJson, ClearanceLayerMeta } from '@/lib/clearance/types';
import { formatFixed, formatStationRange } from '@/lib/stationVolumes/format';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

const PAD = 34;

export interface ClearanceSectionChartProps {
  json: ClearanceJson;
  meta: ClearanceLayerMeta;
  batchId?: string;
  /** Which bin the dock is currently standing at (walk, else brush, else 0). */
  binIndex: number;
  /** Step the walk/selection to another bin — the dock owns that state. */
  onStepBin: (next: number) => void;
  width: number;
  height: number;
  /** Holds the multi-megabyte fetch until the tab is actually on screen. */
  active: boolean;
  /** Rendered when the run carries no section grid (an older worker). */
  fallback: React.ReactNode;
}

/** Resolve one `var(--sv-x, #fallback)` to the concrete rgb the canvas needs. */
const resolveInk = (el: HTMLElement | null, cssValue: string): [number, number, number] => {
  const fallback: [number, number, number] = [220, 70, 90];
  const m = /^var\((--[\w-]+),\s*(.+)\)$/.exec(cssValue.trim());
  let raw = cssValue.trim();
  if (m && el) {
    const fromTheme = getComputedStyle(el).getPropertyValue(m[1]).trim();
    raw = fromTheme || m[2].trim();
  } else if (m) {
    raw = m[2].trim();
  }
  const hex = /^#([0-9a-f]{6})$/i.exec(raw);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const rgb = /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i.exec(raw);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return fallback;
};

export const ClearanceSectionChart = ({
  json,
  meta,
  batchId,
  binIndex,
  onStepBin,
  width,
  height,
  active,
  fallback,
}: ClearanceSectionChartProps) => {
  const { t } = useTranslation('project');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [hover, setHover] = useState<{ col: number; row: number } | null>(null);

  const section = useClearanceSection(meta, batchId, active);
  const bins = section.data?.bins ?? null;

  const bin = json.bins[Math.max(0, Math.min(json.bins.length - 1, binIndex))] ?? null;

  const view = useMemo(
    () => (bins ? sectionViewport(bins, width, height, PAD) : null),
    [bins, width, height],
  );

  const cells = useMemo(
    () => (section.data ? sectionCellsAt(section.data, binIndex) : null),
    [section.data, binIndex],
  );

  // ---------------------------------------------------------------- the grid
  //
  // Drawn at GRID resolution into an offscreen canvas and blitted scaled, with
  // smoothing off. Smoothing would interpolate neighbouring cells into each
  // other, which on a density plot invents returns in empty space — the one
  // thing a clearance drawing must never do.
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv || !bins || !cells || !view) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    cv.width = Math.max(1, Math.round(width * dpr));
    cv.height = Math.max(1, Math.round(height * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const peak = peakCount(cells.count);
    if (peak <= 0) return;

    const violationInk = resolveInk(rootRef.current, CLEARANCE_FILL.violation);
    // Context returns are drawn in a neutral graphite that reads on both
    // themes without competing with the violation ink — the section's subject
    // is what is INSIDE the gauge, and a saturated context would fight it.
    const contextInk: [number, number, number] = [90, 96, 106];

    const { nOffsets, nHeights } = bins;
    const off = document.createElement('canvas');
    off.width = nOffsets;
    off.height = nHeights;
    const octx = off.getContext('2d');
    if (!octx) return;
    const img = octx.createImageData(nOffsets, nHeights);
    for (let row = 0; row < nHeights; row++) {
      // Row 0 is the LOWEST height; ImageData row 0 is the top of the picture.
      const imgRow = nHeights - 1 - row;
      for (let col = 0; col < nOffsets; col++) {
        const src = row * nOffsets + col;
        const total = cells.count[src];
        if (total === 0) continue;
        const bad = cells.violations[src];
        const a = density(total, peak);
        const ink = bad > 0 ? violationInk : contextInk;
        // A cell that is only PARTLY violating still reads as a violation:
        // the question the section answers is "is anything of mine in the
        // gauge here", and averaging it into the context grey would hide a
        // single intruding return behind a hundred clear ones.
        const px = (imgRow * nOffsets + col) * 4;
        img.data[px] = ink[0];
        img.data[px + 1] = ink[1];
        img.data[px + 2] = ink[2];
        img.data[px + 3] = Math.round(255 * (bad > 0 ? Math.max(0.55, a) : a * 0.85));
      }
    }
    octx.putImageData(img, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(off, view.originX, view.originY, view.width, view.height);
  }, [bins, cells, view, width, height]);

  // ------------------------------------------------------------ interaction
  const stepBy = useCallback(
    (delta: number) => onStepBin(Math.max(0, Math.min(json.bins.length - 1, binIndex + delta))),
    [binIndex, json.bins.length, onStepBin],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      // Same keys as the station dock's section, so the two panes are one
      // muscle memory: arrows step a bin, shift steps ten, Home/End jump.
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        stepBy((e.key === 'ArrowRight' ? 1 : -1) * (e.shiftKey ? 10 : 1));
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        onStepBin(e.key === 'Home' ? 0 : json.bins.length - 1);
      }
    },
    [json.bins.length, onStepBin, stepBy],
  );

  const onPointerMove = useCallback(
    (e: PointerEvent<SVGSVGElement>) => {
      if (!bins || !view) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetM = bins.offsetStartM + (e.clientX - rect.left - view.originX) / view.scale;
      const heightM = bins.heightStartM + (view.originY + view.height - (e.clientY - rect.top)) / view.scale;
      const col = colAt(bins, offsetM);
      const row = rowAt(bins, heightM);
      setHover(col >= 0 && col < bins.nOffsets && row >= 0 && row < bins.nHeights ? { col, row } : null);
    },
    [bins, view],
  );

  if (section.unavailable) return <>{fallback}</>;

  if (section.isLoading || section.error) {
    return (
      <div className='flex h-full items-start' data-testid='clearance-section-status'>
        <LoadingBadgeKeyframes />
        <LoadingBadge
          name={t('clearance.section.title')}
          phase={section.error ? 'failed' : 'fetching'}
          progress={section.progress?.totalBytes ? section.progress.loadedBytes / section.progress.totalBytes : undefined}
          loadedBytes={section.progress?.loadedBytes}
          totalBytes={section.progress?.totalBytes ?? undefined}
          error={section.error?.message}
          labels={{
            fetching: t('clearance.section.loading'),
            decoding: t('clearance.section.loading'),
            failed: t('clearance.section.failed'),
          }}
        />
      </div>
    );
  }

  if (!bins || !view || !cells || !bin) return <>{fallback}</>;

  const hoverCell = hover ? hover.row * bins.nOffsets + hover.col : -1;
  const xTicks = sectionTicks(bins.offsetStartM, bins.nOffsets * bins.offsetStepM, view.scale);
  const yTicks = sectionTicks(bins.heightStartM, bins.nHeights * bins.heightStepM, view.scale);
  const datumY = toPxY(view, bins, 0);
  const worst =
    bin.worstOffsetM !== null && bin.worstHeightM !== null
      ? { x: toPxX(view, bins, bin.worstOffsetM), y: toPxY(view, bins, bin.worstHeightM) }
      : null;

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      role='group'
      data-testid='clearance-section'
      aria-label={t('clearance.section.title')}
      onKeyDown={onKeyDown}
      className='relative h-full w-full outline-none focus-visible:ring-2 focus-visible:ring-sky-500'>
      <canvas
        ref={canvasRef}
        data-testid='clearance-section-canvas'
        className='absolute inset-0'
        style={{ width, height }}
      />
      <svg
        width={width}
        height={height}
        className='absolute inset-0'
        role='img'
        aria-label={t('clearance.section.alt', {
          range: formatStationRange({ staStartDisplayM: bin.staStartDisplayM, staEndDisplayM: bin.staEndDisplayM }, 2),
          violations: bin.violationCount.toLocaleString(),
        })}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setHover(null)}>
        {/* Axes first, under everything: they are a reference frame, not
            content, and a tick drawn over the envelope reads as geometry. */}
        <g className='text-slate-400 dark:text-stone-500'>
          {xTicks.map((v) => (
            <g key={`x${v}`}>
              <line
                x1={toPxX(view, bins, v)}
                x2={toPxX(view, bins, v)}
                y1={view.originY}
                y2={view.originY + view.height}
                stroke='currentColor'
                strokeWidth={0.5}
                opacity={0.25}
              />
              <text
                x={toPxX(view, bins, v)}
                y={view.originY + view.height + 14}
                fontSize={10}
                textAnchor='middle'
                fill='currentColor'>
                {v}
              </text>
            </g>
          ))}
          {yTicks.map((v) => (
            <g key={`y${v}`}>
              <line
                x1={view.originX}
                x2={view.originX + view.width}
                y1={toPxY(view, bins, v)}
                y2={toPxY(view, bins, v)}
                stroke='currentColor'
                strokeWidth={0.5}
                opacity={0.25}
              />
              <text x={view.originX - 6} y={toPxY(view, bins, v) + 3} fontSize={10} textAnchor='end' fill='currentColor'>
                {v}
              </text>
            </g>
          ))}
        </g>

        {/* The datum: height 0, i.e. the alignment plus the run's vertical
            offset. Emphasised because it is the line every height on this
            drawing is measured from, and because a road run's whole question
            is how far the envelope's floor sits above it. */}
        <line
          data-testid='clearance-section-datum'
          x1={view.originX}
          x2={view.originX + view.width}
          y1={datumY}
          y2={datumY}
          className='text-slate-500 dark:text-stone-400'
          stroke='currentColor'
          strokeWidth={1}
          strokeDasharray='4 3'
          opacity={0.8}
        />

        {/* The gauge. Stroked, barely filled: the cloud behind it is the
            subject, and a solid gauge would hide the returns it contains. */}
        <polygon
          data-testid='clearance-section-envelope'
          points={profilePolygonPoints(json.profile.vertices, view, bins)}
          fill='currentColor'
          fillOpacity={0.06}
          stroke='currentColor'
          strokeWidth={1.5}
          strokeLinejoin='round'
          className='text-sky-600 dark:text-sky-400'
        />

        {worst && (
          <g data-testid='clearance-section-worst' className='text-rose-600 dark:text-rose-400'>
            <circle cx={worst.x} cy={worst.y} r={5} fill='none' stroke='currentColor' strokeWidth={1.5} />
            <line x1={worst.x - 8} x2={worst.x + 8} y1={worst.y} y2={worst.y} stroke='currentColor' strokeWidth={1} />
            <line x1={worst.x} x2={worst.x} y1={worst.y - 8} y2={worst.y + 8} stroke='currentColor' strokeWidth={1} />
          </g>
        )}

        {hover && hoverCell >= 0 && (
          <g className='text-slate-700 dark:text-stone-200'>
            <rect
              x={toPxX(view, bins, offsetAt(bins, hover.col) - bins.offsetStepM / 2)}
              y={toPxY(view, bins, heightAt(bins, hover.row) + bins.heightStepM / 2)}
              width={Math.max(2, bins.offsetStepM * view.scale)}
              height={Math.max(2, bins.heightStepM * view.scale)}
              fill='none'
              stroke='currentColor'
              strokeWidth={1}
            />
          </g>
        )}
      </svg>

      {/* Readout. Deliberately not a floating tooltip: the numbers that matter
          here (which station, how many violations, how deep) are wanted
          CONSTANTLY, and a tooltip hides them the moment the pointer leaves. */}
      <div
        data-testid='clearance-section-readout'
        className='pointer-events-none absolute top-1 right-1 rounded bg-white/90 px-2 py-1 text-[11px] leading-tight text-slate-700 shadow-sm dark:bg-stone-900/90 dark:text-stone-200'>
        <div className='sv-sta'>
          {formatStationRange({ staStartDisplayM: bin.staStartDisplayM, staEndDisplayM: bin.staEndDisplayM }, 2)}
        </div>
        <div className='sv-num'>
          {t('clearance.section.binViolations', { formatted: bin.violationCount.toLocaleString() })}
        </div>
        {bin.maxIntrusionM !== null && (
          <div className='sv-num'>{t('clearance.section.binWorst', { depth: formatFixed(bin.maxIntrusionM, 3) })}</div>
        )}
        {hover && hoverCell >= 0 && (
          <div className='sv-num mt-1 border-t border-slate-200 pt-1 dark:border-stone-700'>
            {t('clearance.section.cell', {
              offset: formatFixed(offsetAt(bins, hover.col), 2),
              height: formatFixed(heightAt(bins, hover.row), 2),
              points: cells.count[hoverCell].toLocaleString(),
              violations: cells.violations[hoverCell].toLocaleString(),
            })}
          </div>
        )}
      </div>

      <div className='pointer-events-none absolute bottom-1 left-1 text-[10px] text-slate-500 dark:text-stone-400'>
        {t('clearance.section.hint')}
      </div>
    </div>
  );
};
